import React, { useCallback, useEffect, useRef, useState } from "react";
import { DollarCircleOutlined } from "@ant-design/icons";
import { Button, Divider, Form, Input, List, Modal, Result, Spin, message } from "antd";
import { connect } from "dva";
import { EntityType, PriceAdjustType, PriceAdjustmentDirection, QuoteStatus } from "@/utils/enum";
import { adjustQuotesBulk } from "@/services/quotes_new";
import PriceAdjustmentControls from "./PriceAdjustmentControls";
import VehicleItem from "./VehicleItem";

const { TextArea } = Input;

const generatePriceSliderMarks = priceAdjustmentType => {
  const priceSliderMarks = {};

  if (priceAdjustmentType < PriceAdjustType.ByPercentage) {
    for (let i = 0; i <= 1000; i += 100) {
      priceSliderMarks[i] = `$${i}`;
    }
  } else {
    for (let i = 0; i <= 100; i += 10) {
      priceSliderMarks[i] = `${i}%`;
    }
  }

  return priceSliderMarks;
};

const BulkPriceAdjustmentModal = ({ vehicles, shipmentId, refreshPageData, configPerm }) => {
  const priceAdjustmentFormRef = useRef();
  const lowPriceMarginFormRef = useRef();

  const [visible, setVisible] = useState(false);
  const [priceType, setPriceType] = useState(EntityType.Dealer);
  const [priceSliderMarks, setPriceSliderMarks] = useState(generatePriceSliderMarks(PriceAdjustType.ByPercentage));

  const [dealerPriceAdjustmentType, setDealerPriceAdjustmentType] = useState(PriceAdjustType.ByPercentage);
  const [dealerPriceAdjustmentDirection, setDealerPriceAdjustmentDirection] = useState(
    PriceAdjustmentDirection.Increase,
  );
  const [dealerPriceAdjustmentAmount, setDealerPriceAdjustmentAmount] = useState(0);

  const [transporterPriceAdjustmentType, setTransporterPriceAdjustmentType] = useState(PriceAdjustType.ByPercentage);
  const [transporterPriceAdjustmentDirection, setTransporterPriceAdjustmentDirection] = useState(
    PriceAdjustmentDirection.Increase,
  );
  const [transporterPriceAdjustmentAmount, setTransporterPriceAdjustmentAmount] = useState(0);

  const [loadingAdjustingPrices, setLoadingAdjustingPrices] = useState(false);
  const [successAdjustingPrices, setSuccessAdjustingPrices] = useState(false);

  const [priceResetFlag, setPriceResetFlag] = useState(false);
  const [showLowMarginReasonInput, setShowLowMarginReasonInput] = useState(false);

  const handleChangePriceType = useCallback(
    event => {
      const newPriceType = event.target.value;
      const priceAdjustmentType =
        newPriceType === EntityType.Transporter ? transporterPriceAdjustmentType : dealerPriceAdjustmentType;

      setPriceSliderMarks(generatePriceSliderMarks(priceAdjustmentType));
      setPriceType(newPriceType);
    },
    [transporterPriceAdjustmentType, dealerPriceAdjustmentType],
  );

  const handleChangePriceAdjustmentType = useCallback(
    event => {
      const newPriceAdjustmentType = event.target.value;

      setPriceSliderMarks(generatePriceSliderMarks(newPriceAdjustmentType));
      if (priceType === EntityType.Transporter) {
        setTransporterPriceAdjustmentType(newPriceAdjustmentType);
      } else if (priceType === EntityType.Dealer) {
        setDealerPriceAdjustmentType(newPriceAdjustmentType);
      }
    },
    [priceType],
  );

  const handleChangePriceAdjustmentDirection = useCallback(
    event => {
      const newPriceAdjustmentDirection = event.target.value;

      if (priceType === EntityType.Transporter) {
        setTransporterPriceAdjustmentDirection(newPriceAdjustmentDirection);
      } else if (priceType === EntityType.Dealer) {
        setDealerPriceAdjustmentDirection(newPriceAdjustmentDirection);
      }
    },
    [priceType],
  );

  const handleClickAdjustPrices = useCallback(() => {
    setVisible(true);
  }, []);

  const resetValues = useCallback(() => {
    setPriceType(EntityType.Dealer);

    setDealerPriceAdjustmentAmount(0);
    setDealerPriceAdjustmentDirection(PriceAdjustmentDirection.Increase);
    setDealerPriceAdjustmentType(PriceAdjustType.ByPercentage);

    setTransporterPriceAdjustmentAmount(0);
    setTransporterPriceAdjustmentDirection(PriceAdjustmentDirection.Increase);
    setTransporterPriceAdjustmentType(PriceAdjustType.ByPercentage);

    setPriceResetFlag(curr => !curr);
    setPriceSliderMarks(generatePriceSliderMarks(PriceAdjustType.ByPercentage));
    setShowLowMarginReasonInput(false);
  }, []);

  const handleClickCancel = useCallback(() => {
    if (!loadingAdjustingPrices) {
      setVisible(false);
      resetValues();
    }
  }, [loadingAdjustingPrices, resetValues]);

  const getLowMarginMap = useCallback(result => {
    const resultFiltered = {
      ...result,
    };

    // Remove low margin reason from results if found, only need pricing results
    if (resultFiltered.lowPriceMarginReason) {
      delete resultFiltered.lowPriceMarginReason;
    }

    // Will be "vehicleId#newDealerPrice" or "vehicleId#newTransporterPrice", map to just an array of vehicleIds
    const vehicleIds = Object.keys(resultFiltered).map(priceIdentifier => priceIdentifier.split("#")[0]);

    const prices = Object.values(resultFiltered);

    const lowMarginMap = {};

    vehicleIds.forEach(vehicleId => {
      lowMarginMap[vehicleId] = false;
    });

    for (let i = 0; i < prices.length; i += 2) {
      const newDealerPrice = prices[i];
      const newTransporterPrice = prices[i + 1];

      const newMarginDollars = newDealerPrice - newTransporterPrice;
      const newMarginPercent = (newMarginDollars / (newDealerPrice || 1)) * 100;

      if (newMarginPercent < 15) {
        lowMarginMap[vehicleIds[i]] = true;
      }
    }

    return lowMarginMap;
  }, []);

  const createAdjustedQuotesPayloads = useCallback(
    (formResults, lowMarginMap, reason) => {
      const quotesItems = vehicles.map(vehicle => {
        const calculatedAdjustedDealerPrice = Number(formResults[`${vehicle.id}#newDealerPrice`]);
        const calculatedAdjustedTransporterPrice = Number(formResults[`${vehicle.id}#newTransporterPrice`]);

        const newDealerPrice =
          Number.isNaN(calculatedAdjustedDealerPrice) || calculatedAdjustedDealerPrice < 0
            ? 0
            : calculatedAdjustedDealerPrice;
        const newTransporterPrice =
          Number.isNaN(calculatedAdjustedTransporterPrice) || calculatedAdjustedTransporterPrice < 0
            ? 0
            : calculatedAdjustedTransporterPrice;

        return {
          id: vehicle.quote.id,
          shipmentId,
          quoteUpdateDto: {
            shipmentId,
            dealerPrice: newDealerPrice,
            transporterPrice: newTransporterPrice,
            lowMarginReason: lowMarginMap[vehicle.id] ? reason ?? null : null,
          },
        };
      });

      return quotesItems;
    },
    [vehicles, shipmentId],
  );

  const handleClickOk = useCallback(() => {
    priceAdjustmentFormRef.current.validateFields().then(async result => {
      const lowMarginMap = getLowMarginMap(result);
      const hasLowMargin = Object.values(lowMarginMap).some(isLowMargin => isLowMargin);
      let lowPriceMarginResult = null;

      if (hasLowMargin) {
        if (showLowMarginReasonInput) {
          try {
            lowPriceMarginResult = await lowPriceMarginFormRef.current.validateFields();
          } catch {
            return;
          }
        } else {
          setShowLowMarginReasonInput(true);
          return;
        }
      } else {
        setShowLowMarginReasonInput(false);
      }

      setLoadingAdjustingPrices(true);
      const adjustedQuotesPayloads = createAdjustedQuotesPayloads(
        result,
        lowMarginMap,
        lowPriceMarginResult?.lowPriceMarginReason,
      );

      const changesSumamry = adjustedQuotesPayloads.reduce(
        (previousValue, currentValue) => ({
          ...previousValue,
          [currentValue.id]: {
            dealerPrice: currentValue.quoteUpdateDto.dealerPrice,
            transporterPrice: currentValue.quoteUpdateDto.transporterPrice,
          },
        }),
        {},
      );

      adjustQuotesBulk(adjustedQuotesPayloads).then(editResults => {
        let allSuccess = (editResults || []).some(editResult => editResult.status !== QuoteStatus.Completed);

        (editResults || []).forEach(editResult => {
          if (editResult.status === "rejected") {
            message.error(editResult.reason);
            allSuccess = false;
          }
        });

        setTimeout(() => {
          setLoadingAdjustingPrices(false);
          setSuccessAdjustingPrices(allSuccess);

          if (allSuccess) {
            setTimeout(() => {
              setVisible(false);
              setSuccessAdjustingPrices(false);
            }, 1000);
          } else {
            setVisible(false);
          }

          resetValues();

          if (typeof refreshPageData === "function") {
            refreshPageData(changesSumamry);
          }
        }, 1000);
      });
    });
  }, [showLowMarginReasonInput, getLowMarginMap, createAdjustedQuotesPayloads, resetValues, refreshPageData]);

  const calcNewPriceByPercent = useCallback((originalPrice, priceAdjustmentAmount, priceAdjustmentDirection) => {
    const percentDollars = (originalPrice * (priceAdjustmentAmount / 100)).toFixed(2);
    const priceDirectionMultiplier = priceAdjustmentDirection === PriceAdjustmentDirection.Increase ? 1 : -1;

    return (originalPrice + percentDollars * priceDirectionMultiplier).toFixed(2);
  }, []);

  const calcNewPriceByDollars = useCallback((originalPrice, priceAdjustmentAmount, priceAdjustmentDirection) => {
    const priceDirectionMultiplier = priceAdjustmentDirection === PriceAdjustmentDirection.Increase ? 1 : -1;

    return (originalPrice + priceAdjustmentAmount * priceDirectionMultiplier).toFixed(2);
  }, []);

  const calcNewPriceToDollarAmount = useCallback(newPrice => newPrice, []);

  useEffect(() => {
    if (!vehicles || vehicles.length === 0) {
      return;
    }

    const newFormValues = {};

    vehicles.forEach(vehicle => {
      newFormValues[`${vehicle.id}#newDealerPrice`] = vehicle?.quote?.dealerPrice || 0;
      newFormValues[`${vehicle.id}#newTransporterPrice`] = vehicle?.quote?.transporterPrice || 0;
    });

    priceAdjustmentFormRef?.current?.setFieldsValue({
      ...newFormValues,
    });
  }, [vehicles]);

  if (!vehicles || vehicles.length === 0) {
    return null;
  }

  return (
    <>
      {configPerm["CoreService.AdminShipments.Vehicles.EditQuote"] && (
        <Button icon={<DollarCircleOutlined />} onClick={handleClickAdjustPrices}>
          Adjust Prices
        </Button>
      )}
      <Modal
        title="Bulk Price Adjustment"
        visible={visible}
        onCancel={handleClickCancel}
        onOk={handleClickOk}
        okButtonProps={{ disabled: successAdjustingPrices === true }}
        confirmLoading={loadingAdjustingPrices}
        style={{ minWidth: 825 }}
      >
        {successAdjustingPrices ? (
          <Result status="success" title="Prices Updated Successfully" />
        ) : (
          <Spin spinning={loadingAdjustingPrices} tip="Adjusting Prices...">
            <PriceAdjustmentControls
              priceType={priceType}
              handleChangePriceType={handleChangePriceType}
              priceAdjustmentType={
                priceType === EntityType.Transporter ? transporterPriceAdjustmentType : dealerPriceAdjustmentType
              }
              handleChangePriceAdjustmentType={handleChangePriceAdjustmentType}
              priceAdjustmentDirection={
                priceType === EntityType.Transporter
                  ? transporterPriceAdjustmentDirection
                  : dealerPriceAdjustmentDirection
              }
              handleChangePriceAdjustmentDirection={handleChangePriceAdjustmentDirection}
              priceAdjustmentAmount={
                priceType === EntityType.Transporter ? transporterPriceAdjustmentAmount : dealerPriceAdjustmentAmount
              }
              setPriceAdjustmentAmount={
                priceType === EntityType.Transporter
                  ? setTransporterPriceAdjustmentAmount
                  : setDealerPriceAdjustmentAmount
              }
              priceSliderMarks={priceSliderMarks}
              handleClickReset={resetValues}
            />

            <Divider orientation="left">Adjusted Pricing Results</Divider>

            <Form ref={priceAdjustmentFormRef} layout="vertical" requiredMark={false}>
              <List
                dataSource={vehicles}
                rowKey={item => item.vehicleId}
                style={{
                  maxHeight: 400,
                  overflowY: "auto",
                  overflowX: "hidden",
                  border: "1px solid #d9d9d9",
                  padding: "2%",
                }}
                renderItem={item => (
                  <List.Item style={{ marginTop: 8 }}>
                    <List.Item.Meta
                      title={
                        <VehicleItem
                          vehicleData={item}
                          priceType={priceType}
                          dealerPriceAdjustmentType={dealerPriceAdjustmentType}
                          transporterPriceAdjustmentType={transporterPriceAdjustmentType}
                          dealerPriceAdjustmentDirection={dealerPriceAdjustmentDirection}
                          transporterPriceAdjustmentDirection={transporterPriceAdjustmentDirection}
                          dealerPriceAdjustmentAmount={dealerPriceAdjustmentAmount}
                          transporterPriceAdjustmentAmount={transporterPriceAdjustmentAmount}
                          calcNewPriceByPercent={calcNewPriceByPercent}
                          calcNewPriceByDollars={calcNewPriceByDollars}
                          calcNewPriceToDollarAmount={calcNewPriceToDollarAmount}
                          priceAdjustmentFormRef={priceAdjustmentFormRef}
                          priceResetFlag={priceResetFlag}
                          setShowLowMarginReasonInput={setShowLowMarginReasonInput}
                        />
                      }
                    />
                  </List.Item>
                )}
              />
            </Form>
          </Spin>
        )}
      </Modal>
      <Modal
        title="Low Pricing Margin"
        visible={showLowMarginReasonInput}
        onOk={handleClickOk}
        onCancel={() => setShowLowMarginReasonInput(false)}
        confirmLoading={loadingAdjustingPrices}
        afterClose={() => {
          lowPriceMarginFormRef?.current.resetFields();
        }}
      >
        <div style={{ marginBottom: 32 }}>Pricing margin is less than 15%. Please enter reason for low margin.</div>
        <Form ref={lowPriceMarginFormRef} layout="vertical">
          <Form.Item name="lowPriceMarginReason" label="Reason for Low Margin" rules={[{ required: true }]}>
            <TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default connect(({ configs }) => ({
  configPerm: configs.configPerm,
}))(BulkPriceAdjustmentModal);
